import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CheckCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import {
  Stethoscope,
  X,
  FileText,
  Cookie,
  Lock,
  Eye,
  Phone,
  ShieldCheck
} from 'lucide-react';
import PublicHeader from '../../components/public/PublicHeader';
import PublicFooter from '../../components/public/PublicFooter';
import TestimonialsSection from '../../components/public/TestimonialsSection';
import useTypingAnimation from '../../hooks/useTypingAnimation';
import { usePortal } from '../../context/PortalContext';
import DemoRequestPanel from '../../components/public/DemoRequestPanel';
import PortalRegisterModal from '../../components/public/PortalRegisterModal';
import PortalLoginModal from '../../components/public/PortalLoginModal';
import Reveal from '../../components/public/Reveal';
import useGoToApp from '../../hooks/useGoToApp';
import useLowPower from '../../hooks/useLowPower';
import featureCategories from '../../data/featureCategories';
import { CONTACT } from '../../config/contact';

// Temporarily hide public social-proof sections while preserving the implementation.
const SHOW_PUBLIC_SOCIAL_PROOF = false;

// Lazy-load below-the-fold sections so they don't bloat the initial bundle
// or render until the user scrolls near them.
const PatientRegistrationDemo = lazy(() => import('../../components/demos/PatientRegistrationDemo'));
const TriageCheckInDemo = lazy(() => import('../../components/demos/TriageCheckInDemo'));
const VisitTimelineDemo = lazy(() => import('../../components/demos/VisitTimelineDemo'));
const AppointmentsDemo = lazy(() => import('../../components/demos/AppointmentsDemo'));
const BPWeightTrendsDemo = lazy(() => import('../../components/demos/BPWeightTrendsDemo'));
const IDFormatsDemo = lazy(() => import('../../components/demos/IDFormatsDemo'));
const FamilyLinksDemo = lazy(() => import('../../components/demos/FamilyLinksDemo'));
const ConsultationQueueDemo = lazy(() => import('../../components/demos/ConsultationQueueDemo'));
const ClinicalParametersDemo = lazy(() => import('../../components/demos/ClinicalParametersDemo'));
const OrderSetsDemo = lazy(() => import('../../components/demos/OrderSetsDemo'));
const ClinicalWorkspaceDemo = lazy(() => import('../../components/demos/ClinicalWorkspaceDemo'));
const VisitClosureDemo = lazy(() => import('../../components/demos/VisitClosureDemo'));
const LabTestCatalogDemo = lazy(() => import('../../components/demos/LabTestCatalogDemo'));
const LabOrderQueueDemo = lazy(() => import('../../components/demos/LabOrderQueueDemo'));
const SampleCollectionDemo = lazy(() => import('../../components/demos/SampleCollectionDemo'));
const EnterResultsDemo = lazy(() => import('../../components/demos/EnterResultsDemo'));
const ReportVerificationDemo = lazy(() => import('../../components/demos/ReportVerificationDemo'));
const ReportPrintingDemo = lazy(() => import('../../components/demos/ReportPrintingDemo'));
const ReorderAlertsDemo = lazy(() => import('../../components/demos/ReorderAlertsDemo'));
const BulkEntryDemo = lazy(() => import('../../components/demos/BulkEntryDemo'));
const StockCountDemo = lazy(() => import('../../components/demos/StockCountDemo'));
const IntelligenceDemo = lazy(() => import('../../components/demos/IntelligenceDemo'));
const GroupChatRoomsDemo = lazy(() => import('../../components/demos/GroupChatRoomsDemo'));
const MessageForwardingDemo = lazy(() => import('../../components/demos/MessageForwardingDemo'));
const StaffDirectoryDemo = lazy(() => import('../../components/demos/StaffDirectoryDemo'));
const UnreadCountBadgesDemo = lazy(() => import('../../components/demos/UnreadCountBadgesDemo'));
const POSDemo = lazy(() => import('../../components/demos/POSDemo'));
const InvoiceVersioningDemo = lazy(() => import('../../components/demos/InvoiceVersioningDemo'));
const FinancialAlertsDemo = lazy(() => import('../../components/demos/FinancialAlertsDemo'));
const PaymentsLedgerDemo = lazy(() => import('../../components/demos/PaymentsLedgerDemo'));
const PaymentPlansDemo = lazy(() => import('../../components/demos/PaymentPlansDemo'));
const ReturnsRefundsDemo = lazy(() => import('../../components/demos/ReturnsRefundsDemo'));
const DrugsCatalogDemo = lazy(() => import('../../components/demos/DrugsCatalogDemo'));
const PartialDispensingDemo = lazy(() => import('../../components/demos/PartialDispensingDemo'));
const PrescriptionFormDemo = lazy(() => import('../../components/demos/PrescriptionFormDemo'));
const PharmacyReturnsDemo = lazy(() => import('../../components/demos/PharmacyReturnsDemo'));
const DispensedHistoryDemo = lazy(() => import('../../components/demos/DispensedHistoryDemo'));

// Wrapper that defers rendering until the section is near the viewport.
const DeferredMount = ({ children, fallback }) => {
  const ref = useRef(null);
  const [isNearViewport, setIsNearViewport] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        setIsNearViewport(true);
        observer.disconnect();
      }
    }, { rootMargin: '300px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref}>{isNearViewport ? children : fallback}</div>;
};

const TYPING_WORDS = ['Simple', 'Affordable', 'Effortless', 'Profitable'];

// Concise feature summary displayed below the interactive timeline.
const CORE_FEATURES = [
  {
    title: 'Patient registration & scheduling',
    description: 'Register patients, manage appointments, and keep every visit organized from the front desk.'
  },
  {
    title: 'Clinical documentation',
    description: 'Capture SOAP notes, vitals, diagnoses, allergies, care plans, and follow-up decisions in one workspace.'
  },
  {
    title: 'Laboratory & results',
    description: 'Manage lab orders, sample collection, result entry, verification, and critical value alerts.'
  },
  {
    title: 'Pharmacy & dispensing',
    description: 'Prescribe and dispense medicines with batch, expiry, interaction, and partial-fill tracking.'
  },
  {
    title: 'Billing & payments',
    description: 'Create invoices, accept multiple payment methods, track balances, and manage the revenue cycle.'
  },
  {
    title: 'Inventory control',
    description: 'Track stock, batches, suppliers, purchase orders, transfers, counts, and reorder alerts.'
  },
  {
    title: 'Inpatient & care management',
    description: 'Coordinate admissions, beds, ward rounds, medication administration, discharge, and chronic care.'
  },
  {
    title: 'Maternal & child health',
    description: 'Support ANC, labour and delivery, postnatal care, immunization, and child health workflows.'
  },
  {
    title: 'Insurance & claims',
    description: 'Link policies, submit itemized claims, track approvals, manage appeals, and monitor settlements.'
  },
  {
    title: 'Reports & analytics',
    description: 'Turn operational data into dashboards, performance insights, financial reports, and exports.'
  },
  {
    title: 'Role-based access',
    description: 'Give every team member the right access with configurable roles, permissions, and audit trails.'
  },
  {
    title: 'Secure staff communication',
    description: 'Keep teams connected with encrypted direct messages, group rooms, notifications, and presence.'
  },
  {
    title: 'Radiology & imaging',
    description: 'Manage imaging orders, preparation, structured reports, studies, and result delivery.'
  },
  {
    title: 'Emergency operations',
    description: 'Prioritize urgent patients with triage, emergency beds, clinical scores, alerts, and handover.'
  },
  {
    title: 'Configuration & localization',
    description: 'Adapt the platform to your facility with branding, catalogs, currencies, languages, and settings.'
  }
];

const HOW_IT_WORKS_STEPS = [
  {
    label: '01 · Register once',
    title: 'Give every patient a clear digital identity',
    description: 'The front desk captures the patient’s essential details and Ospito+ HMS creates a unique identifier for reliable records from the very first visit.'
  },
  {
    label: '03 · Recognise every visit',
    title: 'Turn return visits into a connected story',
    description: 'That identifier links subsequent visits to the same patient, so staff can find history, appointments, documents, and trends without starting over.'
  },
  {
    label: '02 · Check in & triage',
    title: 'Move the patient from arrival to the right queue',
    description: 'Registration staff record vitals, visit details, concerns, and billing information, then check the patient in for the appropriate care team.'
  },
  {
    label: '04 · See who needs care',
    title: 'Give providers a live view of the waiting room',
    description: 'Doctors and care teams can see who is waiting, understand priority and wait time, and open the patient’s context before the consultation begins.'
  },
  {
    label: '05 · Consult & decide',
    title: 'Document the encounter while the decision is fresh',
    description: 'Clinical notes, vitals, diagnoses, prescriptions, care plans, and follow-ups are captured in one workspace instead of scattered across paper and disconnected systems.'
  },
  {
    label: '06 · Route the work',
    title: 'Send every request to the people who act on it',
    description: 'Laboratory, radiology, pharmacy, procedures, and other service points receive digital orders in their queues with the information they need to respond.'
  },
  {
    label: '07 · Close the loop',
    title: 'Bring results and next actions back to the care team',
    description: 'As services complete their work, results and statuses flow back into the patient record, helping providers act faster and keeping the patient journey moving.'
  }
];

const MODULE_SUBTABS = {
  patients: [
    { id: 'registry', label: 'Patient Registry', type: 'demo', component: 'PatientRegistrationDemo' },
    { id: 'triage', label: 'Triage Check-in', type: 'demo', component: 'TriageCheckInDemo' },
    { id: 'appointments', label: 'Appointments', type: 'demo', component: 'AppointmentsDemo' },
    { id: 'timeline', label: 'Visit Timeline', type: 'demo', component: 'VisitTimelineDemo' },
    { id: 'bp_weight', label: 'BP & Weight Trends', type: 'demo', component: 'BPWeightTrendsDemo' },
    { id: 'id_formats', label: 'ID Formats', type: 'demo', component: 'IDFormatsDemo' },
    { id: 'family_links', label: 'Family Links', type: 'demo', component: 'FamilyLinksDemo' },
  ],
  clinical: [
    { id: 'queue', label: 'Consultation Queue', type: 'demo', component: 'ConsultationQueueDemo' },
    { id: 'parameters', label: 'Clinical Parameters', type: 'demo', component: 'ClinicalParametersDemo' },
    { id: 'order_sets', label: 'Order Sets', type: 'demo', component: 'OrderSetsDemo' },
    { id: 'workspace', label: 'Clinical Workspace', type: 'demo', component: 'ClinicalWorkspaceDemo' },
    { id: 'closure', label: 'Visit Closure', type: 'demo', component: 'VisitClosureDemo' },
  ],
  laboratory: [
    { id: 'queue', label: 'Order Queue', type: 'demo', component: 'LabOrderQueueDemo' },
    { id: 'collection', label: 'Sample Collection', type: 'demo', component: 'SampleCollectionDemo' },
    { id: 'results', label: 'Enter Results', type: 'demo', component: 'EnterResultsDemo' },
  ],
  inventory: [
    { id: 'reorder', label: 'Reorder Alerts', type: 'demo', component: 'ReorderAlertsDemo' },
    { id: 'receive', label: 'Receive Entry', type: 'demo', component: 'BulkEntryDemo' },
    { id: 'count', label: 'Stock Count', type: 'demo', component: 'StockCountDemo' },
    { id: 'intelligence', label: 'Intelligence', type: 'demo', component: 'IntelligenceDemo' },
  ],
  notifications: [
    { id: 'rooms', label: 'Group Chat Rooms', type: 'demo', component: 'GroupChatRoomsDemo' },
    { id: 'forwarding', label: 'Message Forwarding', type: 'demo', component: 'MessageForwardingDemo' },
    { id: 'directory', label: 'Staff Directory', type: 'demo', component: 'StaffDirectoryDemo' },
    { id: 'badges', label: 'Unread Badges', type: 'demo', component: 'UnreadCountBadgesDemo' },
  ],
  billing: [
    { id: 'pos', label: 'POS', type: 'demo', component: 'POSDemo' },
    { id: 'invoices', label: 'Invoice Versioning', type: 'demo', component: 'InvoiceVersioningDemo' },
    { id: 'alerts', label: 'Financial Alerts', type: 'demo', component: 'FinancialAlertsDemo' },
    { id: 'ledger', label: 'Payments Ledger', type: 'demo', component: 'PaymentsLedgerDemo' },
    { id: 'plans', label: 'Payment Plans', type: 'demo', component: 'PaymentPlansDemo' },
    { id: 'returns', label: 'Returns & Refunds', type: 'demo', component: 'ReturnsRefundsDemo' },
  ],
  pharmacy: [
    { id: 'drugs', label: 'Drugs & Sundries', type: 'demo', component: 'DrugsCatalogDemo' },
    { id: 'dispensing', label: 'Partial Dispensing', type: 'demo', component: 'PartialDispensingDemo' },
    { id: 'prescriptions', label: 'Prescription Form', type: 'demo', component: 'PrescriptionFormDemo' },
    { id: 'returns', label: 'Returns', type: 'demo', component: 'PharmacyReturnsDemo' },
    { id: 'dispensed', label: 'Dispensed', type: 'demo', component: 'DispensedHistoryDemo' },
  ],
};

// Hero image - Doctor portrait (optimized PNG with transparency)
const HERO_IMAGE_WEBP = '/screenshots/hero-doctor.webp';
const HERO_IMAGE_PNG = '/screenshots/hero-doctor.png';

// Animated multi-counter hook — animates all values in a SINGLE rAF loop
// instead of one loop per counter.  Pass an array of { end, duration }.
const useCountUpMulti = (targets, shouldStart, reduceMotion = false) => {
  const [counts, setCounts] = useState(targets.map(() => 0));
  const completedRef = useRef(false);

  useEffect(() => {
    if (!shouldStart || completedRef.current) return undefined;

    const prefersReducedMotion = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || prefersReducedMotion) {
      setCounts(targets.map(t => t.end));
      completedRef.current = true;
      return undefined;
    }

    let startTime = null;
    let animationFrame;
    let lastUpdateTime = 0;
    const maxDuration = Math.max(...targets.map(t => t.duration));

    const animate = (timestamp) => {
      if (startTime === null) startTime = timestamp;
      const elapsed = timestamp - startTime;

      if (timestamp - lastUpdateTime >= 1000 / 30 || elapsed >= maxDuration) {
        lastUpdateTime = timestamp;
        const nextCounts = targets.map(t => {
          const percentage = Math.min(elapsed / t.duration, 1);
          const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
          return Math.floor(t.end * easeOutQuart);
        });
        setCounts(previous => previous.some((count, index) => count !== nextCounts[index]) ? nextCounts : previous);
      }

      if (elapsed < maxDuration) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        completedRef.current = true;
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldStart, reduceMotion]);

  return counts;
};

// Legal Content Data
const legalContent = {
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'November 2025',
    sections: [
      {
        title: 'Introduction',
        content: 'Ospito+ HMS ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains our data practices. As a self-hosted or cloud-hosted platform, your organization maintains full ownership and control of all healthcare data entered into the system.'
      },
      {
        title: 'Your Data Belongs to You',
        content: 'We do NOT collect, access, or process any of your organization\'s operational data. All patient records, medical information, billing data, staff details, and clinical documentation remain exclusively within your organization\'s isolated database. We have no visibility into your healthcare operations.'
      },
      {
        title: 'What We Track (For Security Only)',
        content: 'For platform security and abuse prevention, we collect minimal system telemetry:',
        items: [
          'Login timestamps and session duration',
          'Failed authentication attempts (to detect brute-force attacks)',
          'General system health metrics (uptime, error rates)',
          'Subscription status and billing for cloud-hosted plans'
        ]
      },
      {
        title: 'Data Security',
        content: 'Your data is protected by industry-standard security measures including: JWT-based authentication with 30-minute token expiry, two-factor authentication (TOTP), bcrypt password hashing, role-based access control with 9 predefined roles and granular permissions, account lockout after 5 failed login attempts, and encrypted data transmission via HTTPS.'
      },
      {
        title: 'Multi-Tenant Data Isolation',
        content: 'Ospito+ HMS operates on a multi-tenant architecture where each healthcare facility\'s data is completely isolated using UUID-based tenant identification. Your organization\'s data is never accessible to other tenants, to us, or to any third party. All database queries are automatically filtered by tenant ID.'
      },
      {
        title: 'Data Retention & Deletion',
        content: 'We never delete your data without your consent. You have two options: (1) Export all your data at any time in standard formats, or (2) Delete your account, which permanently removes your entire dataset. For security and resource management, accounts that have been inactive for more than 2 years may be subject to deletion after notification attempts.'
      },
      {
        title: 'Your Rights',
        items: [
          'Full ownership of all data entered into your organization\'s instance',
          'Export your complete dataset at any time',
          'Delete your account and all associated data permanently',
          'Request information about what minimal telemetry we collect'
        ]
      },
      {
        title: 'Contact Us',
        content: `For privacy-related inquiries, contact us at ${CONTACT.email} or call ${CONTACT.phoneDisplay}.`
      }
    ]
  },
  terms: {
    title: 'Terms of Service',
    lastUpdated: 'November 2025',
    sections: [
      {
        title: 'Agreement to Terms',
        content: 'By accessing or using Ospito+ HMS, you agree to be bound by these Terms of Service. If you are using the service on behalf of a healthcare organization, you represent that you have authority to bind that organization to these terms.'
      },
      {
        title: 'Service Description',
        content: 'Ospito+ HMS is a comprehensive hospital management system providing: patient administration, clinical documentation, pharmacy management with drug safety features, laboratory and radiology information systems, billing and financial accounting, inventory management, inpatient department management, maternal and child health tracking, emergency department operations, and reporting and analytics.'
      },
      {
        title: 'Subscription Plans',
        items: [
          'Premium Plan: Core modules including Patients, Clinical, Pharmacy, Laboratory, Billing, and Inventory, plus 7-day demo access',
          'Enterprise Plan: All Premium features plus Emergency, IPD, MCH, Procedures, dedicated support, custom integrations, and on-premise deployment options',
          'Add-on Modules: Available for Premium plan subscribers at additional cost'
        ]
      },
      {
        title: 'User Responsibilities',
        items: [
          'Maintain the confidentiality of login credentials',
          'Ensure all users complete appropriate training',
          'Use the system only for legitimate healthcare purposes',
          'Comply with applicable healthcare regulations (e.g., HIPAA, local health data laws)',
          'Report any security incidents or unauthorized access immediately',
          'Maintain accurate and up-to-date patient information'
        ]
      },
      {
        title: 'Prohibited Uses',
        items: [
          'Sharing login credentials between users',
          'Attempting to access other tenants\' data',
          'Reverse engineering or copying the software',
          'Using the system for non-healthcare purposes',
          'Uploading malicious content or attempting to compromise system security',
          'Reselling or sublicensing access to the platform'
        ]
      },
      {
        title: 'Data Ownership',
        content: 'You retain full ownership of all patient data, clinical records, and other information entered into the system. We do not claim any ownership rights over your data. Upon termination, you may request a complete export of your data in standard formats.'
      },
      {
        title: 'Service Availability',
        content: 'We strive for 99.9% uptime for cloud-hosted deployments. Scheduled maintenance will be communicated in advance. We are not liable for interruptions due to factors beyond our control, including internet connectivity issues at your facility.'
      },
      {
        title: 'Limitation of Liability',
        content: 'Ospito+ HMS is a tool to assist healthcare delivery but does not replace professional medical judgment. We are not liable for clinical decisions made using the system. Our total liability is limited to the fees paid for the service in the 12 months preceding any claim.'
      },
      {
        title: 'Termination',
        content: 'Either party may terminate the subscription with 30 days written notice. Upon termination, you will have 30 days to export your data. We may suspend access immediately for Terms violations or non-payment.'
      }
    ]
  },
  cookies: {
    title: 'Cookie Policy',
    lastUpdated: 'November 2025',
    sections: [
      {
        title: 'What Are Cookies',
        content: 'Cookies are small text files stored on your device when you use our platform. They help us provide a secure, personalized experience and remember your preferences.'
      },
      {
        title: 'Essential Cookies',
        content: 'These cookies are necessary for the platform to function and cannot be disabled.',
        items: [
          'Authentication Token: Securely identifies your session (JWT stored in localStorage)',
          'Tenant Identifier: Ensures you access only your organization\'s data',
          'Session Security: Prevents unauthorized access and CSRF attacks',
          'User Preferences: Remembers your selected language and theme'
        ]
      },
      {
        title: 'Functional Cookies',
        content: 'These enhance your experience but are not strictly necessary.',
        items: [
          'Dark Mode Preference: Remembers your light/dark theme selection',
          'Language Setting: Stores your preferred language (English, French, or Swahili)',
          'Sidebar State: Remembers whether you prefer the collapsed or expanded sidebar',
          'Currency Display: Stores your preferred currency format',
          'Timezone: Saves your timezone for accurate date/time display'
        ]
      },
      {
        title: 'Security Cookies',
        content: 'These cookies help protect your account and our platform.',
        items: [
          'Two-Factor Authentication: Temporarily stores 2FA verification status',
          'Login Attempt Tracking: Helps prevent brute-force attacks',
          'Session Timeout: Automatically logs you out after 30 minutes of inactivity'
        ]
      },
      {
        title: 'What We Don\'t Use',
        items: [
          'Third-party advertising cookies',
          'Social media tracking cookies',
          'Cross-site tracking cookies',
          'Analytics cookies that identify individuals'
        ]
      },
      {
        title: 'Managing Cookies',
        content: 'Essential and security cookies cannot be disabled as they are required for the platform to function securely. You can manage functional cookies through your browser settings or the Settings page within the application. Note that disabling certain cookies may affect your user experience.'
      },
      {
        title: 'Data Storage',
        content: 'We use browser localStorage for authentication tokens and user preferences. This data remains on your device and is cleared when you log out or clear your browser data. Session data is encrypted and automatically expires.'
      }
    ]
  }
};

const Landing = () => {
  const location = useLocation();
  const { portalUser, portalLoading } = usePortal();
  const lowPower = useLowPower();
  const [hasCoarsePointer] = useState(() => typeof window !== 'undefined' && (
    (typeof window.matchMedia === 'function' && window.matchMedia('(any-pointer: coarse)').matches)
    || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
  ));
  const reduceHeroMotion = lowPower || hasCoarsePointer;
  const [activeTab, setActiveTab] = useState('patients');
  const [activeSubTab, setActiveSubTab] = useState('registry');
  const [startCounting, setStartCounting] = useState(false);
  const [isFacilityModalOpen, setIsFacilityModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [activeLegalModal, setActiveLegalModal] = useState(null); // 'privacy' | 'terms' | 'cookies' | null
  const [activeSection, setActiveSection] = useState('home');
  const statsRef = useRef(null);
  const homeSectionRef = useRef(null);
  
  // Typing animation using shared hook
  const typedText = useTypingAnimation(TYPING_WORDS, 100, 50, 2000, !reduceHeroMotion, 'Affordable');

  const goToApp = useGoToApp(portalUser);

  // Default sub-tab per module (overrides the first-tab default)
  const DEFAULT_SUBTAB = {
    patients: 'timeline',
    clinical: 'workspace',
    laboratory: 'results',
    billing: 'pos',
    notifications: 'rooms',
    inventory: 'receive',
    pharmacy: 'drugs',
  };

  // Reset sub-tab to the default (or first) when the module (activeTab) changes
  useEffect(() => {
    const subtabs = MODULE_SUBTABS[activeTab];
    if (subtabs && subtabs.length > 0) {
      const defaultId = DEFAULT_SUBTAB[activeTab];
      const exists = defaultId && subtabs.some(t => t.id === defaultId);
      setActiveSubTab(exists ? defaultId : subtabs[0].id);
    }
  }, [activeTab]);

  const isDemoExpired = Boolean(
    portalUser && (portalUser.status === 'demo_expired' || portalUser.status === 'suspended' || (!portalUser.is_eligible_for_demo && !portalUser.can_access_app))
  );

  // Scroll handling - prevent hash scroll on initial load
  const hasScrolled = useRef(false);

  // Use layoutEffect to run before browser paints
  useEffect(() => {
    // On mount, if there's a hash, remove it and scroll to top
    if (!hasScrolled.current && window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
      window.scrollTo(0, 0);
      hasScrolled.current = true;
    } else if (!hasScrolled.current) {
      window.scrollTo(0, 0);
      hasScrolled.current = true;
    }
  }, []);

  // Handle hash navigation from menu clicks
  useEffect(() => {
    if (!hasScrolled.current) return; // Skip on initial mount

    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  // Handle legal modal via ?legal=privacy|terms|cookies query param.
  // Re-evaluated on every navigation so clicking a legal link while already
  // on the landing page opens the modal (previously only worked on fresh load).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const legalParam = params.get('legal');
    // Use hasOwnProperty to avoid prototype-pollution keys like __proto__
    if (legalParam && Object.prototype.hasOwnProperty.call(legalContent, legalParam)) {
      setActiveLegalModal(legalParam);
    }
    // Auto-open demo modal via ?demo=1 (used by payment failure redirect)
    if (params.get('demo') === '1') {
      setIsFacilityModalOpen(true);
    }
  }, [location.search]);

  // Track which section is in view for header nav highlighting
  useEffect(() => {
    const sections = [
      { id: 'home', ref: homeSectionRef },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.dataset.section);
          }
        });
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );

    sections.forEach(({ ref }) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  // Intersection Observer for stats
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStartCounting(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const [checkinPct, billingPct, labMultiplier] = useCountUpMulti(
    [
      { end: 40, duration: 1500 },
      { end: 60, duration: 1800 },
      { end: 3, duration: 1200 },
    ],
    startCounting,
    reduceHeroMotion
  );

  const renderPrimaryCta = (compact = false) => {
    const primaryClass = compact
      ? 'group relative inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold tracking-tight text-white transition-all duration-300 bg-orange-500 rounded-2xl hover:bg-orange-600 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500'
      : 'group relative inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-bold tracking-tight text-white transition-all duration-300 bg-orange-500 rounded-2xl hover:bg-orange-600 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500';

    if (portalLoading) {
      return (
        <div className={`${compact ? 'px-6 py-3 text-sm' : 'px-7 py-3.5 text-base'} inline-flex items-center justify-center font-semibold text-transparent bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse`}>
          <span>Loading…</span>
        </div>
      );
    }
    if (portalUser?.can_access_app) {
      return <button onClick={goToApp} className={primaryClass}><span>Go to App</span><ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></button>;
    }
    if (isDemoExpired) {
      return <Link to="/pricing" className={primaryClass}><span>Upgrade Now</span><ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></Link>;
    }
    if (portalUser) {
      return <button onClick={() => setIsFacilityModalOpen(true)} className={primaryClass}><span>Request Demo</span><ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></button>;
    }
    return <button onClick={() => setIsFacilityModalOpen(true)} className={primaryClass}><span>Request Demo</span><ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></button>;
  };

  return (
    <div className={`min-h-screen bg-white dark:bg-gray-900 font-sans selection:bg-teal-500/30 animate-pageFadeIn landing-page${reduceHeroMotion ? ' low-power' : ''}`}>
      <PublicHeader activeSection={activeSection} />

      {/* Home Section */}
      <div ref={homeSectionRef} data-section="home">
      {/* Hero Section - Gemini Style with Original Content */}
      <div className={`relative pt-24 pb-[60px] sm:pt-28 lg:pt-32 lg:pb-[80px] overflow-hidden${reduceHeroMotion ? ' low-power' : ''}`}>
        {/* Background Gradient - Static SVG image for performance */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img 
            src="/images/hero-gradient.svg" 
            alt="" 
            className="absolute inset-0 w-full h-full object-cover dark:hidden"
            aria-hidden="true"
          />
          {/* Dark mode background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 hidden dark:block" />

          {/* Animated aurora orbs — purely decorative */}
          <div className="absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-teal-300/30 dark:bg-teal-500/10 blur-3xl animate-blob" aria-hidden="true" />
          <div className="absolute top-1/3 -right-32 w-[26rem] h-[26rem] rounded-full bg-cyan-300/30 dark:bg-cyan-500/10 blur-3xl animate-blob animation-delay-2000" aria-hidden="true" />
          <div className="absolute -bottom-32 left-1/3 w-[24rem] h-[24rem] rounded-full bg-violet-300/20 dark:bg-violet-500/10 blur-3xl animate-blob animation-delay-4000" aria-hidden="true" />
        </div>

        <div className="relative z-10 max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[0.88fr_1.12fr] gap-10 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="w-full max-w-3xl text-center lg:text-left">

              {/* Main Heading with Typing Animation */}
              <h1 className="text-[3.25rem] md:text-[4.25rem] lg:text-[5rem] font-extrabold tracking-tighter text-gray-900 dark:text-white mb-8 leading-[1.05]">
                <span className="block">Hospital</span>
                <span className="block">Management</span>
                <span className="relative block mt-2">
                  {/* Invisible sizer: reserves the tallest state ("Made " plus a long
                      word) so the typing animation cannot change the heading height and
                      shift everything below it. Adapts to any width and font metrics. */}
                  <span className="invisible font-extrabold" aria-hidden="true">Made Affordable</span>
                  <span className="absolute inset-0 font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 via-cyan-500 to-violet-500 dark:from-teal-400 dark:via-cyan-400 dark:to-violet-400 animate-gradient-x">
                    Made {typedText}
                    {!reduceHeroMotion && <span className="inline-block w-1 h-10 sm:h-12 lg:h-16 bg-teal-600 dark:bg-teal-400 ml-2 animate-pulse align-middle"></span>}
                  </span>
                </span>
              </h1>

              <p className="mt-6 text-xl md:text-[1.35rem] font-light text-gray-600 dark:text-gray-400 leading-relaxed tracking-tight">
                Avoid the billing leaks, stock losses, and slow queues that quietly cost many facilities
                millions each year — <span className="font-semibold text-gray-900 dark:text-white">from front desk to pharmacy, ward to lab, on one platform your staff actually use.</span>
              </p>

              <div ref={statsRef} className="mt-10 grid grid-cols-2 gap-5 sm:gap-6 text-center lg:text-left">
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">
                    {checkinPct}%
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-semibold tracking-tight">Faster Check-in</div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 font-medium">Patient registration time</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                    {billingPct}%
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-semibold tracking-tight">Fewer Billing Errors</div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 font-medium">Revenue leakage stopped</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                    {labMultiplier}x
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-semibold tracking-tight">Faster Lab Turnaround</div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 font-medium">Sample to verified report</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">0</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-semibold tracking-tight">Data Breaches</div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 font-medium">E2E encrypted platform</div>
                </div>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-3.5 justify-center lg:justify-start items-stretch">
                {renderPrimaryCta()}
                {!isDemoExpired && (
                  <Link
                    to="/pricing"
                    className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-bold tracking-tight text-gray-700 dark:text-gray-200 bg-white/60 dark:bg-white/5 backdrop-blur-xl border border-gray-300/60 dark:border-white/10 rounded-2xl hover:border-gray-900/30 dark:hover:border-white/30 hover:bg-white/80 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-300"
                  >
                    <span>See Plans & Pricing</span>
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                )}
              </div>

              <div className="mt-7 flex flex-wrap justify-center lg:justify-start gap-x-5 gap-y-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5"><CheckCircleIcon className="w-3.5 h-3.5 text-teal-500" /> No credit card required</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircleIcon className="w-3.5 h-3.5 text-teal-500" /> Full access — every module</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircleIcon className="w-3.5 h-3.5 text-teal-500" /> Cancel anytime</span>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="relative flex items-center justify-center min-w-0 self-end -mb-[60px] lg:-mb-20">
              <div className="absolute inset-8 rounded-full bg-gradient-to-tr from-teal-400/25 via-cyan-300/20 to-violet-300/25 blur-3xl animate-glow pointer-events-none" aria-hidden="true" />
              <div className="relative w-full max-w-3xl">
                <picture>
                  <source srcSet={HERO_IMAGE_WEBP} type="image/webp" />
                  <img
                    src={HERO_IMAGE_PNG}
                    alt="Healthcare professional using Ospito+"
                    className={`relative w-full h-auto object-contain${reduceHeroMotion ? '' : ' animate-imageFadeIn'}`}
                    width={748}
                    height={800}
                    style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.08))' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </picture>

                <div className="absolute right-[7.5rem] sm:right-[7.5rem] top-[13rem] sm:top-[17rem] bg-white dark:bg-gray-800 rounded-xl shadow-lg px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-100 dark:border-gray-700 animate-float">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
                      <Stethoscope className="w-4 h-4 text-white" />
                    </div>
                    <div><div className="text-xs font-bold text-gray-900 dark:text-white">258 Diagnoses</div></div>
                  </div>
                </div>

                <div className="absolute left-0 sm:-left-4 bottom-16 sm:bottom-24 bg-white dark:bg-gray-800 rounded-xl shadow-lg px-3 py-2 border border-gray-100 dark:border-gray-700 animate-float-delayed">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                      <CheckCircleIcon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-[11px] sm:text-xs font-semibold text-gray-700 dark:text-gray-300">1,994 Clinical Macros</span>
                  </div>
                </div>

                <div className="hidden sm:block absolute right-3 bottom-40 bg-white dark:bg-gray-800 rounded-xl shadow-lg px-3 py-2 border border-gray-100 dark:border-gray-700 animate-float">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
                      <ShieldCheck className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">2,044 Signs & Symptoms</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - Compact Ultramodern Design */}
      <div id="products" className="pt-8 pb-8 relative scroll-mt-20 overflow-visible">
        {/* Subtle Background Pattern — kept within the section bounds. It must
            not exceed 100% height: this section is overflow-visible, so a taller
            overlay adds its overflow to the document and leaves a large empty
            gap below the footer. The gradient still fades to transparent before
            the section ends, so there is no hard boundary. */}
        <div className="absolute inset-x-0 top-0 h-full bg-[radial-gradient(ellipse_70%_50%_at_50%_25%,_var(--tw-gradient-stops))] from-teal-50/40 via-transparent to-transparent dark:from-teal-900/10 pointer-events-none z-0"></div>

        <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section Header */}
          <Reveal className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-3 tracking-tight">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 dark:from-white dark:via-gray-200 dark:to-white">Transform your</span>{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-600 to-cyan-600 dark:from-teal-400 dark:to-cyan-400 animate-gradient-x">facility today.</span>
            </h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-teal-500 to-cyan-500 mx-auto rounded-full mb-4 shadow-lg shadow-teal-500/30"></div>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto font-medium">
              Every module your team will ever need — beautifully designed, deeply integrated, and ready on day one.
            </p>
          </Reveal>

          <div className="relative mt-10 mb-14">
            <div className="absolute -inset-10 rounded-[3rem] bg-gradient-to-tr from-teal-400/15 via-cyan-300/10 to-violet-300/15 blur-3xl animate-glow pointer-events-none" aria-hidden="true" />
            <div className="absolute -top-8 right-10 w-40 h-40 rounded-full bg-cyan-300/20 dark:bg-cyan-500/10 blur-3xl animate-blob pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-8 left-12 w-44 h-44 rounded-full bg-violet-300/15 dark:bg-violet-500/10 blur-3xl animate-blob animation-delay-2000 pointer-events-none" aria-hidden="true" />
            <DeferredMount fallback={<div className="h-[634px] flex items-center justify-center text-xs text-gray-400">Loading visit timeline…</div>}>
              <div className="relative w-full drop-shadow-[0_24px_35px_rgba(13,148,136,0.12)]">
                <Suspense fallback={<div className="h-[634px] flex items-center justify-center text-xs text-gray-400">Loading visit timeline…</div>}>
                  <VisitTimelineDemo lowPower={reduceHeroMotion} />
                </Suspense>
              </div>
            </DeferredMount>
          </div>

          {/* Core Features Table */}
          <Reveal className="mt-16 mb-14">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400">One connected platform</p>
              <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Core features and functionality</h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm sm:text-base leading-relaxed text-gray-600 dark:text-gray-400">
                Everything your facility needs to coordinate care, control operations, and serve patients better.
              </p>
            </div>

            <div className="mt-10 overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse text-left">
                <caption className="sr-only">Core Ospito+ HMS features and functionality</caption>
                <tbody>
                  {Array.from({ length: Math.ceil(CORE_FEATURES.length / 3) }, (_, rowIndex) => (
                    <tr key={rowIndex} className="border-t border-teal-100 dark:border-gray-700/70">
                      {CORE_FEATURES.slice(rowIndex * 3, rowIndex * 3 + 3).map((feature, featureIndex) => (
                        <td
                          key={feature.title}
                          className={`w-1/3 align-top p-5 sm:p-6 ${featureIndex > 0 ? 'border-l border-teal-100 dark:border-gray-700/70' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300">
                              <CheckCircleIcon className="h-4 w-4" />
                            </span>
                            <div>
                              <h4 className="text-sm font-bold leading-snug text-gray-900 dark:text-white">{feature.title}</h4>
                              <p className="mt-1.5 text-xs leading-relaxed text-gray-600 dark:text-gray-400">{feature.description}</p>
                            </div>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          {/* How Ospito+ HMS Works */}
          <Reveal className="mb-20">
            <div className="mx-auto max-w-4xl text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400">From arrival to action</p>
              <h3 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">How Ospito+ HMS works</h3>
              <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500" />
              <p className="mx-auto mt-4 max-w-3xl text-sm sm:text-base leading-relaxed text-gray-600 dark:text-gray-400">
                Watch the patient journey move from the front desk to the care team — with every handoff visible, connected, and ready for action.
              </p>
            </div>

            {/* The first two moments of the patient journey */}
            <div className="mt-10 grid grid-cols-1 items-stretch gap-10 xl:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
              <div className="flex min-w-0 flex-col">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-300">The front desk</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-1 items-start xl:pr-4">
                  <div className="group relative flex w-full max-w-[44rem] gap-4 rounded-2xl border border-gray-200/80 bg-white/80 p-4 transition-all duration-200 hover:border-teal-200 hover:bg-white hover:shadow-md dark:border-gray-700/70 dark:bg-gray-900/60 dark:hover:border-teal-800 dark:hover:bg-gray-900/90 sm:gap-6 sm:p-5">
                    <div className="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-teal-500 to-cyan-600 text-sm font-bold text-white shadow-md dark:border-gray-950">01</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-teal-600 dark:text-teal-400">01 · Register once</p>
                      <h5 className="mt-1 text-base font-bold text-gray-900 dark:text-white sm:text-lg">Give every patient a clear digital identity</h5>
                      <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        The front desk captures the patient’s essential details and Ospito+ HMS creates a unique identifier for reliable records from the very first visit.
                      </p>
                    </div>
                    <div className="hidden self-center text-teal-300 transition-transform duration-200 group-hover:translate-x-1 sm:block dark:text-teal-700" aria-hidden="true">→</div>
                  </div>
                </div>

                <div className="mt-3 flex items-start xl:pr-4">
                  <div className="group relative flex w-full max-w-[44rem] gap-4 rounded-2xl border border-gray-200/80 bg-white/80 p-4 transition-all duration-200 hover:border-teal-200 hover:bg-white hover:shadow-md dark:border-gray-700/70 dark:bg-gray-900/60 dark:hover:border-teal-800 dark:hover:bg-gray-900/90 sm:gap-6 sm:p-5">
                    <div className="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-teal-500 to-cyan-600 text-sm font-bold text-white shadow-md dark:border-gray-950">02</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-teal-600 dark:text-teal-400">02 · Check in &amp; triage</p>
                      <h5 className="mt-1 text-base font-bold text-gray-900 dark:text-white sm:text-lg">Move the patient from arrival to the right queue</h5>
                      <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        Registration staff record vitals, visit details, concerns, and billing information, then check the patient in for the appropriate care team.
                      </p>
                    </div>
                    <div className="hidden self-center text-teal-300 transition-transform duration-200 group-hover:translate-x-1 sm:block dark:text-teal-700" aria-hidden="true">→</div>
                  </div>
                </div>
              </div>

              <div className="flex min-w-0 flex-col">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-teal-600 dark:text-teal-300">The triage desk</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-1 items-start">
                  <div className="w-full">
                    <DeferredMount fallback={<div className="flex h-[420px] items-center justify-center text-xs text-gray-400">Loading triage check-in…</div>}>
                      <Suspense fallback={<div className="flex h-[420px] items-center justify-center text-xs text-gray-400">Loading triage check-in…</div>}>
                        <TriageCheckInDemo lowPower={reduceHeroMotion} className="min-h-[260px]" />
                      </Suspense>
                    </DeferredMount>
                  </div>
                </div>
              </div>
            </div>

            {/* Connected workflow */}
            <div className="mt-12">
              <div className="relative">
                <div className="absolute bottom-8 left-5 top-8 w-px bg-gradient-to-b from-purple-300 via-teal-400 to-cyan-300 dark:from-purple-700 dark:via-teal-600 dark:to-cyan-700 md:hidden" aria-hidden="true" />
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {HOW_IT_WORKS_STEPS.map((step, index) => (index === 0 || index === 2 ? null : (
                    <div key={step.label} className={`group relative flex gap-4 rounded-2xl border border-gray-200/80 bg-white/80 p-4 transition-all duration-200 hover:border-teal-200 hover:bg-white hover:shadow-md dark:border-gray-700/70 dark:bg-gray-900/60 dark:hover:border-teal-800 dark:hover:bg-gray-900/90 sm:gap-6 sm:p-5 ${index === 6 ? 'md:col-span-2' : ''}`}>
                      <div className="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-teal-500 to-cyan-600 text-sm font-bold text-white shadow-md dark:border-gray-950">
                        {step.label.slice(0, 2)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-teal-600 dark:text-teal-400">{step.label}</p>
                        <h5 className="mt-1 text-base font-bold text-gray-900 dark:text-white sm:text-lg">{step.title}</h5>
                        <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{step.description}</p>
                      </div>
                      <div className="hidden self-center text-teal-300 transition-transform duration-200 group-hover:translate-x-1 sm:block dark:text-teal-700" aria-hidden="true">→</div>
                    </div>
                  )))}
                </div>
              </div>

            </div>
          </Reveal>

        </div>
      </div>
      </div>

      {SHOW_PUBLIC_SOCIAL_PROOF && <TestimonialsSection reduceMotion={reduceHeroMotion} />}

      <PublicFooter />

      {/* Demo request panel — from public Request Demo buttons */}
      <DemoRequestPanel
        isOpen={isFacilityModalOpen}
        onClose={() => setIsFacilityModalOpen(false)}
      />

      {/* Portal Register Modal — from "Start 7-Day Demo" when not logged in */}
      <PortalRegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSwitchToLogin={() => setIsLoginModalOpen(true)}
      />

      {/* Portal Login Modal — from "Already have an account? Sign in" */}
      <PortalLoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSwitchToRegister={() => setIsRegisterModalOpen(true)}
      />

      {/* Legal Modal - Ultramodern Design */}
      {activeLegalModal && Object.prototype.hasOwnProperty.call(legalContent, activeLegalModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop with blur */}
          <div 
            className="absolute inset-0 bg-gray-950/70 backdrop-blur-md"
            onClick={() => setActiveLegalModal(null)}
          ></div>
          
          {/* Modal Container */}
          <div className="relative w-full max-w-xl animate-fadeIn">
            {/* Floating Close Button */}
            <button
              onClick={() => setActiveLegalModal(null)}
              className="absolute -top-2 -right-2 z-10 w-8 h-8 rounded-full bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center hover:scale-110 transition-transform group"
            >
              <X className="w-4 h-4 text-gray-500 group-hover:text-gray-900 dark:group-hover:text-white" />
            </button>

            {/* Modal Card */}
            <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-white/20 dark:border-gray-700/50">
              
              {/* Compact Header */}
              <div className="relative px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                {/* Accent Line */}
                <div className={`absolute top-0 left-0 right-0 h-0.5 ${
                  activeLegalModal === 'privacy' 
                    ? 'bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500' 
                    : activeLegalModal === 'terms'
                    ? 'bg-gradient-to-r from-violet-500 via-purple-500 to-violet-500'
                    : 'bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500'
                }`}></div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                      activeLegalModal === 'privacy' 
                        ? 'bg-blue-100 dark:bg-blue-900/40' 
                        : activeLegalModal === 'terms'
                        ? 'bg-violet-100 dark:bg-violet-900/40'
                        : 'bg-amber-100 dark:bg-amber-900/40'
                    }`}>
                      {activeLegalModal === 'privacy' && <Eye className={`w-4 h-4 text-blue-600 dark:text-blue-400`} />}
                      {activeLegalModal === 'terms' && <FileText className={`w-4 h-4 text-violet-600 dark:text-violet-400`} />}
                      {activeLegalModal === 'cookies' && <Cookie className={`w-4 h-4 text-amber-600 dark:text-amber-400`} />}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white">{legalContent[activeLegalModal].title}</h3>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">Updated {legalContent[activeLegalModal].lastUpdated}</p>
                    </div>
                  </div>
                  
                  {/* Quick Nav Pills */}
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
                    <button
                      onClick={() => setActiveLegalModal('privacy')}
                      className={`px-2 py-1 rounded-md text-[10px] font-medium transition-all ${
                        activeLegalModal === 'privacy'
                          ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      Privacy
                    </button>
                    <button
                      onClick={() => setActiveLegalModal('terms')}
                      className={`px-2 py-1 rounded-md text-[10px] font-medium transition-all ${
                        activeLegalModal === 'terms'
                          ? 'bg-white dark:bg-gray-700 text-violet-600 dark:text-violet-400 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      Terms
                    </button>
                    <button
                      onClick={() => setActiveLegalModal('cookies')}
                      className={`px-2 py-1 rounded-md text-[10px] font-medium transition-all ${
                        activeLegalModal === 'cookies'
                          ? 'bg-white dark:bg-gray-700 text-amber-600 dark:text-amber-400 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      Cookies
                    </button>
                  </div>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="px-5 py-4 overflow-y-auto max-h-[60vh] space-y-4 scrollbar-thin">
                {legalContent[activeLegalModal].sections.map((section, idx) => (
                  <div key={idx} className="group">
                    <h4 className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${
                      activeLegalModal === 'privacy' 
                        ? 'text-blue-700 dark:text-blue-400' 
                        : activeLegalModal === 'terms'
                        ? 'text-violet-700 dark:text-violet-400'
                        : 'text-amber-700 dark:text-amber-400'
                    }`}>
                      <span className={`w-1 h-1 rounded-full ${
                        activeLegalModal === 'privacy' 
                          ? 'bg-blue-500' 
                          : activeLegalModal === 'terms'
                          ? 'bg-violet-500'
                          : 'bg-amber-500'
                      }`}></span>
                      {section.title}
                    </h4>
                    
                    {section.content && (
                      <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed pl-2.5">
                        {section.content}
                      </p>
                    )}
                    
                    {section.items && (
                      <ul className="mt-1.5 pl-2.5 space-y-1">
                        {section.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-[11px] text-gray-600 dark:text-gray-400">
                            <div className={`w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              activeLegalModal === 'privacy' 
                                ? 'bg-blue-100 dark:bg-blue-900/30' 
                                : activeLegalModal === 'terms'
                                ? 'bg-violet-100 dark:bg-violet-900/30'
                                : 'bg-amber-100 dark:bg-amber-900/30'
                            }`}>
                              <CheckCircleIcon className={`w-2 h-2 ${
                                activeLegalModal === 'privacy' 
                                  ? 'text-blue-600 dark:text-blue-400' 
                                  : activeLegalModal === 'terms'
                                  ? 'text-violet-600 dark:text-violet-400'
                                  : 'text-amber-600 dark:text-amber-400'
                              }`} />
                            </div>
                            <span className="leading-tight">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>

              {/* Compact Footer */}
              <div className="px-5 py-3 bg-gray-50/80 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                    <Lock className="w-3 h-3" />
                    <span>Your data stays yours</span>
                  </div>
                  <button
                    onClick={() => setActiveLegalModal(null)}
                    className={`px-4 py-1.5 rounded-lg text-white text-[11px] font-semibold transition-all hover:scale-105 shadow-lg ${
                      activeLegalModal === 'privacy' 
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 shadow-blue-500/25' 
                        : activeLegalModal === 'terms'
                        ? 'bg-gradient-to-r from-violet-500 to-purple-600 shadow-violet-500/25'
                        : 'bg-gradient-to-r from-amber-500 to-orange-500 shadow-amber-500/25'
                    }`}
                  >
                    Got it
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
